import { $isListNode } from '@lexical/list';
import { $convertFromMarkdownString, TextMatchTransformer, Transformer } from '@lexical/markdown';
import { $isTableNode } from '@lexical/table';
import { $insertNodeToNearestRoot } from '@lexical/utils';
import { $isElementNode, $isParagraphNode, LexicalNode } from 'lexical';
import {
  $createSuggestionNode,
  $isSuggestionNode,
  $sourceSuggestion,
} from '../../nodes/SuggestionNode';
import { Source } from '../../types';

const VALID_SOURCES: Source[] = ['default', 'note', 'inline'];

function isValidSource(source: string): source is Source {
  return VALID_SOURCES.includes(source as Source);
}

function textContainsBlockElements(text: string): boolean {
  return /<table|<ul|<ol|<div|<blockquote/i.test(text);
}

function normalizeLineBreaks(text: string): string {
  return text.replace(/<br\s*\/?>/gi, '\n');
}

function shouldBeBlockSuggestion(nodes: LexicalNode[]): boolean {
  if (nodes.length === 1 && $isParagraphNode(nodes[0])) {
    return false;
  }

  for (const node of nodes) {
    if ($isTableNode(node) || $isListNode(node)) {
      return true;
    }
    if ($isElementNode(node) && !node.isInline()) {
      return true;
    }
  }

  return false;
}

export const createInlineSuggestionTransformer = (
  transformers: Transformer[],
): TextMatchTransformer => ({
  type: 'text-match',
  dependencies: [],

  regExp: /<suggestion(?: source="([^"]*)")?>(.*?)<\/suggestion>/,
  importRegExp: /<suggestion(?: source="([^"]*)")?>(.*?)<\/suggestion>/,
  export: (node: LexicalNode, traverseChildren) => {
    if (!$isSuggestionNode(node)) {
      return null;
    }

    const source = $sourceSuggestion(node);
    const output: string[] = [];

    if (source !== 'default') {
      output.push(`<suggestion source="${source}">`);
    } else {
      output.push('<suggestion>');
    }

    output.push(traverseChildren(node));
    output.push('</suggestion>');
    return output.join('');
  },

  replace: (node, match) => {
    const sourceAttr = match[1];
    const rawContent = match[2];
    const source: Source = sourceAttr && isValidSource(sourceAttr) ? sourceAttr : 'default';

    // <br/> tags inside markdown tables break rendering
    const textContent = normalizeLineBreaks(rawContent);

    // $convertFromMarkdownString tries to clear the entire root node, need temp container
    const tempContainer = $createSuggestionNode(source, true);
    $insertNodeToNearestRoot(tempContainer);
    $convertFromMarkdownString(textContent, transformers, tempContainer, true);

    const parsedChildren = tempContainer.getChildren();

    if (parsedChildren.length === 0) {
      tempContainer.remove();
      node.remove();
      return;
    }

    const hasBlockContent =
      textContainsBlockElements(textContent) || shouldBeBlockSuggestion(parsedChildren);
    const suggestion = $createSuggestionNode(source, hasBlockContent);

    if (hasBlockContent) {
      parsedChildren.forEach(child => suggestion.append(child));
      tempContainer.remove();

      const nodeParent = node.getParent();
      if ($isParagraphNode(nodeParent)) {
        nodeParent.insertAfter(suggestion);
        node.remove();
        if (nodeParent.isEmpty()) nodeParent.remove();
      } else {
        node.replace(suggestion);
      }
    } else {
      if (parsedChildren.length === 1 && $isParagraphNode(parsedChildren[0])) {
        parsedChildren[0].getChildren().forEach(child => suggestion.append(child));
      } else {
        parsedChildren.forEach(child => suggestion.append(child));
      }

      tempContainer.remove();
      node.replace(suggestion);
    }

    suggestion.selectNext();
  },
});
